import { MvnMessage } from "@utils/lang";
import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import { AuthLayout } from "@layouts/Auth";
import { useCreateUserMutation, useLoginMutation } from "@hooks/mutation/user/mutation";
import { IsUserPasswordValid } from "maven-lib/dist/consts/validator";
import { classNames } from "maven-lib/dist/utils/misc";
import { SignUpEmailVerificationDialog } from "./partials/SignUpEmailVerificationDialog";
import { preparedAuthLink as preparedAuthLinkSSO, preparedCallbackLink, retrieveSSOPayload } from "./SSO";
import { CrCheckbox } from "@maven-surface/components/base/CrCheckbox";
import { CrRequestButton } from "@maven-surface/components/base/CrRequestButton";
import { useRoute } from "@maven-surface/hooks";
import { AUTH_USER_LANG, SSO_USER_VERIFICATION_CODE_LANG } from "@maven-msg/lang";
import { AUTH_USER_VERIFICATION_CODE_LANG } from "@maven-msg/lang/auth/userVerificationCode";
import { LoLang, MavenService } from "@maven-rest/common/common.types";
import { UserVerificationCodeFlax } from "@maven-rest/sso/UserVerificationCode.flax";
import { DEVICE_ENV_KEY, LOCALE_LANG_KEY, SESSION_COOKIE_DOMAIN, sessionSlice, setCookie } from "@maven-rest/states/reducers";
import { getBrowserDefaultLanguage } from "@maven-rest/states/reducers/session";
import { getDefaultHostname } from "@sso-lib/host";

const IS_NEED_NOTIFY_LAST_LOGINTIME = "isNeedNotifyLastLoginTime";

enum ConsentType {
  Agreement = "AGREEMENT",
  Personal = "PERSONAL",
  Marketing = "MARKETING",
}

export function SignUp() {
  const emailRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { query } = useRoute();
  const redirectTo = query.redirect || (state as any)?.from?.pathname;
  const service = MavenService.MavenClinicalCloud;

  const [emailVal, setEmailVal] = useState("");
  const [passwordVal, setPasswordVal] = useState("");
  const [passwordConfirmVal, setPasswordConfirmVal] = useState("");
  const [nameVal, setNameVal] = useState("");

  const [agreementAll, setAgreementAll] = useState(false);
  const [agreementLegal, setAgreementLegal] = useState(false);
  const [agreementPersonal, setAgreementPersonal] = useState(false);
  const [agreementMarketing, setAgreementMarketing] = useState(false);
  const [agreementAge, setAgreementAge] = useState(false);

  const [emailVerificationDialogVisibility, setEmailVerificationDialogVisibility] = useState(false);
  const [emailVerificationCodeUuid, setEmailVerificationCodeUuid] = useState("");

  const localStorageLanguage = localStorage.getItem(LOCALE_LANG_KEY) as LoLang;
  const lang = localStorageLanguage || getBrowserDefaultLanguage();

  const preparedAuthLink = (url: string) =>
    preparedAuthLinkSSO(
      `${url}${query.redirect ? `${url.includes("?") ? "&" : "?"}redirect=${encodeURIComponent(String(query.redirect))}` : ""}`
    );

  const { redirectHostname } = useMemo(
    () =>
      getDefaultHostname({
        from: process.env.REACT_APP_SERVICE_SSO_FE_DOMAIN_FROM || "",
        to: process.env.REACT_APP_SERVICE_SSO_FE_DOMAIN_TO || "",
        defaultVal: process.env.REACT_APP_AUTH_FE_URL ?? redirectTo,
        hostname: window.location.hostname,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [process.env.REACT_APP_SERVICE_SSO_FE_DOMAIN_FROM, process.env.REACT_APP_SERVICE_SSO_FE_DOMAIN_TO, process.env.REACT_APP_AUTH_FE_URL]
  );
  const defaultRedirect = `${redirectHostname.startsWith("http") ? "" : `${window.location.protocol}//`}${redirectHostname}`;

  const { mutate: createUser, isLoading: isCreateLoading } = useCreateUserMutation();
  const { mutate: login } = useLoginMutation();

  const disabled =
    !String(emailVal).trim() ||
    !isEmail(emailVal) ||
    !passwordVal ||
    passwordVal !== passwordConfirmVal ||
    !IsUserPasswordValid(passwordVal) ||
    !String(nameVal).trim() ||
    !agreementAge ||
    !agreementLegal ||
    !agreementPersonal;

  return (
    <AuthLayout>
      <SignUpEmailVerificationDialog
        visibility={emailVerificationDialogVisibility}
        email={emailVal}
        verificationCodeUuid={emailVerificationCodeUuid}
        onVerified={(code) => {
          setEmailVerificationDialogVisibility(false);

          createUser(
            {
              email: emailVal,
              password: passwordVal,
              name: nameVal,
              verificationCodeUuid: emailVerificationCodeUuid,
              verificationCode: code,
              agreeMarketing: agreementMarketing,
            },
            {
              onSuccess: (resp) => {
                const { token, expiryAt } = resp.user.deviceToken;

                const isLocal = !window.location.protocol.startsWith("https") || process.env.REACT_APP_STAGE === "local";

                setCookie(DEVICE_ENV_KEY, token, new Date(expiryAt), {
                  domain: SESSION_COOKIE_DOMAIN,
                  secure: !isLocal,
                  sameSite: !isLocal ? "None" : undefined,
                });

                login(
                  { email: emailVal, password: passwordVal, service, deviceToken: token },
                  {
                    onSuccess: (resp) => {
                      sessionStorage.setItem(IS_NEED_NOTIFY_LAST_LOGINTIME, "true");
                      dispatch(
                        sessionSlice.actions.signIn({
                          ...resp,
                          redirectTo: retrieveSSOPayload()
                            ? preparedCallbackLink({ token: resp.authToken.token, expiryAt: resp.authToken.expiryAt }) ?? ""
                            : preparedAuthLink(defaultRedirect) ?? "",
                        })
                      );
                    },
                  }
                );
              },
            }
          );
        }}
        onCancel={() => setEmailVerificationDialogVisibility(false)}
      />
      <div className="header">
        <h2 className="title">Sign up</h2>
        <p className="help">
          Any problem?{" "}
          <a href="https://www.jnpmedi.com" target="_blank" rel="noopener noreferrer">
            Ask us anything!
          </a>
        </p>
      </div>
      <div className="form">
        <fieldset className="type--label">
          <legend>Sign in</legend>
          <label htmlFor="email">
            Email *
            <input
              ref={emailRef}
              id="email"
              placeholder="Email"
              maxLength={256}
              className={classNames("email", !!emailVal && !isEmail(emailVal) && "state--error")}
              type="email"
              onChange={(e) => setEmailVal(e.target.value)}
            />
          </label>
          <label htmlFor="password">
            Password *
            <input
              id="password"
              placeholder="Password"
              maxLength={1024}
              className={classNames("password", !!passwordVal && !IsUserPasswordValid(passwordVal) && "state--error")}
              type="password"
              onChange={(e) => setPasswordVal(e.target.value)}
            />
            <em>{MvnMessage(AUTH_USER_LANG.USER_PASSWORD_RULE)}</em>
          </label>
          <label htmlFor="passwordConfirm">
            Password Confirm *
            <input
              id="passwordConfirm"
              placeholder="Password Confirm"
              className={classNames("password", !!passwordConfirmVal && passwordVal !== passwordConfirmVal && "state--error")}
              type="password"
              onChange={(e) => setPasswordConfirmVal(e.target.value)}
            />
          </label>
          <label htmlFor="name">
            Name *
            <input
              id="name"
              placeholder="Name"
              maxLength={32}
              className="name"
              type="text"
              onChange={(e) => {
                setNameVal(e.target.value);
              }}
            />
          </label>
          <label htmlFor="legal-all" className="type--check legal--all">
            <CrCheckbox
              id="legal-all"
              checked={agreementAll}
              onValueChange={(e) => {
                setAgreementAll(e);
                setAgreementLegal(e);
                setAgreementPersonal(e);
                setAgreementMarketing(e);
                setAgreementAge(e);
              }}
            />
            <p>{MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_AGREE_ALL)}</p>
          </label>
          <label htmlFor="legal-agreement" className="type--check legal--agreement">
            <CrCheckbox
              id="legal-agreement"
              checked={agreementLegal}
              onValueChange={(e) => {
                setAgreementLegal(e);
                setAgreementAll(e && agreementPersonal && agreementMarketing && agreementAge);
              }}
            />
            <p>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_LEGAL_PREFIX)}{" "}
              <a href={getConsentGuideUrl(ConsentType.Agreement, lang)} target="_blank" rel="noopener noreferrer">
                {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_LEGAL)}
              </a>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_LEGAL_SUFFIX)}{" "}
              <strong>{MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_REQUIRED)}</strong>
            </p>
          </label>
          <label htmlFor="legal-personal" className="type--check legal--agree-personal">
            <CrCheckbox
              id="legal-personal"
              checked={agreementPersonal}
              onValueChange={(e) => {
                setAgreementPersonal(e);
                setAgreementAll(agreementLegal && e && agreementMarketing && agreementAge);
              }}
            />
            <p>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_PERSONAL_PREFIX)}{" "}
              <a href={getConsentGuideUrl(ConsentType.Personal, lang)} target="_blank" rel="noopener noreferrer">
                {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_PERSONAL_DATA)}
              </a>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_PERSONAL_SUFFIX)}{" "}
              <strong>{MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_REQUIRED)}</strong>
            </p>
          </label>
          <label htmlFor="legal-marketing" className="type--check legal--agree-marketing">
            <CrCheckbox
              id="legal-marketing"
              checked={agreementMarketing}
              onValueChange={(e) => {
                setAgreementMarketing(e);
                setAgreementAll(agreementLegal && agreementPersonal && e && agreementAge);
              }}
            />
            <p>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_MARKETING_PREFIX)}{" "}
              <a href={getConsentGuideUrl(ConsentType.Marketing, lang)} target="_blank" rel="noopener noreferrer">
                {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_MARKETING_LEGAL)}
              </a>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_MARKETING_SUFFIX)}{" "}
              <strong>{MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_OPTIONAL)}</strong>
            </p>
          </label>
          <label htmlFor="legal-age" className="type--check legal--agree-age">
            <CrCheckbox
              id="legal-age"
              checked={agreementAge}
              onValueChange={(e) => {
                setAgreementAge(e);
                setAgreementAll(agreementLegal && agreementPersonal && agreementMarketing && e);
              }}
            />
            <p>
              {MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_AGE)}{" "}
              <strong>{MvnMessage(AUTH_USER_LANG.USER_SIGNUP_AGREEMENT_REQUIRED)}</strong>
            </p>
          </label>
        </fieldset>
        <div className="button">
          <CrRequestButton
            api={() => ({ ...UserVerificationCodeFlax.createUserVerificationCodeForSignUp({ email: emailVal }) })}
            delegate={{
              success: (resp) => {
                setEmailVerificationCodeUuid(resp.userVerificationCode.uuid);
                setEmailVerificationDialogVisibility(true);

                return MvnMessage(SSO_USER_VERIFICATION_CODE_LANG.SSO_USER_VERIFICATION_CODE_SIGN_UP_EMAIL_SENT);
              },
              error: (error) => MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_SEND_FAIL, { message: error }),
            }}
            disabled={disabled}
            loading={isCreateLoading}
            color="primary"
          >
            Sign Up
          </CrRequestButton>
          <hr />
          <Link to={preparedAuthLink("/auth/sign-in")} className="cr-button primary">
            <em>Already registered?</em> Sign In.
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

function getConsentGuideUrl(consentType: ConsentType, lang: LoLang): string {
  const KO_AGREEMENT_URL = "https://mvn.do/legal/agreement";
  const EN_AGREEMENT_URL = "https://mvn.do/legal/agreement/en";

  const KO_PERSONAL_URL = "https://mvn.do/legal/personal";
  const EN_PERSONAL_URL = "https://mvn.do/legal/personal/en";

  const KO_MARKETING_URL = "https://mvn.do/legal/marketing";
  const EN_MARKETING_URL = "https://mvn.do/legal/marketing/en";

  function getAgreementUrl(lang: LoLang): string {
    if (lang === LoLang.KO) return KO_AGREEMENT_URL;
    return EN_AGREEMENT_URL;
  }
  function getPersonalUrl(lang: LoLang): string {
    if (lang === LoLang.KO) return KO_PERSONAL_URL;
    return EN_PERSONAL_URL;
  }
  function getMarketingUrl(lang: LoLang): string {
    if (lang === LoLang.KO) return KO_MARKETING_URL;
    return EN_MARKETING_URL;
  }

  switch (consentType) {
    case ConsentType.Agreement:
      return getAgreementUrl(lang);
    case ConsentType.Personal:
      return getPersonalUrl(lang);
    case ConsentType.Marketing:
      return getMarketingUrl(lang);
  }
}
